"use client";

import { useAnimationFrame, useMotionValue, useMotionValueEvent, useTransform } from "motion/react";
import { useRef } from "react";
import { mergeProps, useMove } from "react-aria";
import * as m from "motion/react-m";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";

export function MediaProgressBar({
  mediaRef,
  inverse = false,
  size = "default",
}: {
  inverse?: boolean;
  mediaRef: React.RefObject<HTMLAudioElement | HTMLVideoElement | null>;
  size?: "default" | "xs" | "sm" | "lg";
}) {
  const t = useTranslations("ClientKeys.MediaProgressBar");
  const containerRef = useRef<HTMLButtonElement>(null);
  const p = useMotionValue(0);
  const transform = useTransform(p, [0, 1], ["translateX(-100%)", "translateX(0%)"]);
  const movingRef = useRef(false);
  const moveStartRelativeRef = useRef(0);

  useAnimationFrame(() => {
    if (!mediaRef.current) return;
    if (movingRef.current) return;
    const currentTime = mediaRef.current.currentTime;
    const duration = mediaRef.current.duration;
    if (!duration || isNaN(currentTime) || isNaN(duration)) return;
    const progress = currentTime / duration;
    p.set(progress);
  });

  useMotionValueEvent(p, "change", p => {
    containerRef.current?.setAttribute("aria-valuenow", String(Math.round(p * 100)));
  });

  const { moveProps } = useMove({
    onMoveStart: () => {
      movingRef.current = true;
    },
    onMoveEnd: () => {
      movingRef.current = false;
    },
    onMove: event => {
      const width = containerRef.current?.clientWidth ?? 0;
      if (event.pointerType === "keyboard") {
        const media = mediaRef.current;
        if (!media || !Number.isFinite(media.duration) || media.duration <= 0) return;
        const direction = Math.sign(event.deltaX);
        if (direction === 0) return;
        const newTime = Math.min(media.duration, Math.max(0, media.currentTime + direction * 10));
        media.currentTime = newTime;
        const newProgress = newTime / media.duration;
        moveStartRelativeRef.current = newProgress;
        p.set(newProgress);
        return;
      }
      moveStartRelativeRef.current = Math.min(
        Math.max(0, moveStartRelativeRef.current + (width > 0 ? event.deltaX / width : 0)),
        1,
      );
      const newProgress = moveStartRelativeRef.current;
      p.set(newProgress);
      if (mediaRef.current) {
        mediaRef.current.currentTime = newProgress * mediaRef.current.duration;
      }
    },
  });

  function onPointerDown(event: React.PointerEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width;
    moveStartRelativeRef.current = width > 0 ? Math.max(0, Math.min(1, (event.clientX - rect.left) / width)) : 0;
    if (mediaRef.current) {
      const duration = mediaRef.current.duration;
      if (!duration || isNaN(duration)) return;
      mediaRef.current.currentTime = moveStartRelativeRef.current * duration;
    }
  }

  return (
    <div className='tw-w-full tw-flow-root'>
      <button
        ref={containerRef}
        type='button'
        role='slider'
        aria-label={t("seek")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={0}
        className='tw-block tw-w-full tw-group/progress tw-outline-none tw-py-4 -tw-my-4'
        {...mergeProps(moveProps, { onPointerDown })}
      >
        <div
          className={clsx(
            "tw-rounded-full tw-backdrop-blur-sm tw-overflow-hidden group-focus-visible/progress:tw-outline-focus",
            inverse ? "tw-bg-white/20" : "tw-bg-black/5",
          )}
        >
          <m.div
            className={clsx(
              "tw-origin-left tw-rounded-full",
              inverse ? "tw-bg-white" : "tw-bg-black",
              size === "default" && "tw-h-1",
              size === "xs" && "tw-h-0.25",
              size === "sm" && "tw-h-0.5",
              size === "lg" && "tw-h-2",
            )}
            style={{ transform }}
          />
        </div>
      </button>
    </div>
  );
}
